import { useCallback, useEffect, useMemo } from 'react';
import {
    selectByOptions,
    SelectByOptionsValue,
    resolutionOptions,
    serializeHeatmap,
} from 'components/widgets/RiskDensity/RiskDensity.utils';
import moment from 'moment-timezone';
import { useProgramCriticalAnalysisStore } from 'store/programCriticalAnalysis.store';

export const useRiskDensityLogic = ({
    ui,
    api,
    setDateRange,
    setDateRangeOption,
    setEndProjectDate,
    setStartProjectDate,
    setRiskDensityBy,
    setResolution,
    setSelectBy,
}) => {
    const { update } = useProgramCriticalAnalysisStore();

    const serialized = useMemo(() => {
        return serializeHeatmap(api.riskDensityBy.data?.heatMapItemResponseList, ui.state.resolution);
    }, [api.riskDensityBy.data?.heatMapItemResponseList, ui.state.resolution]);

    // handlers
    const handleSelectBy = (value: ISelectOption<SelectByOptionsValue> | null) => {
        if (value) {
            setRiskDensityBy(value.value);
            setSelectBy(value.value);
        }
    };

    const handleDateRangeSelection = (value: { startDate: number | null; endDate: number | null }) => {
        setDateRange(value);
    };

    const handleResolutionFilter = (value: ISelectOption<boolean>) => {
        setResolution(value.value);
    };

    const setDateOnDelayDriver = useCallback(
        (value: string) => {
            const date = serialized.find((item) => item.id === value);
            if (date) {
                const m = moment(date.date);
                update({
                    dateFilter: {
                        value: m.format('yyyyMM'),
                        label: m.format('MMM yyyy'),
                    },
                });
            }
        },
        [serialized],
    );

    const selectByValue = selectByOptions.find((item) => item.value === ui.state.selectBy);
    const resolutionValue = resolutionOptions.find((item) => item.value === ui.state.resolution);

    //useEffects

    // reset Date range whenever one of the global filters has been changed.
    useEffect(() => {
        if (!ui.inPinboard) {
            setDateRange({
                startDate: null,
                endDate: null,
            });
            setDateRangeOption('wholeProject');
            setEndProjectDate(null);
            setStartProjectDate(null);
        }
    }, [ui.inPinboard, ui.state.activityCodes, ui.state.milestoneFragnet, ui.state.tags, ui.state.wbs]);

    useEffect(() => {
        if (api.riskDensityBy.data && !ui.state.endProjectDate && !ui.state.startProjectDate) {
            setEndProjectDate(api.riskDensityBy.data?.endProjectDate);
            setStartProjectDate(api.riskDensityBy.data?.startProjectDate);
        }
    }, [
        ui.state.endProjectDate,
        ui.state.startProjectDate,
        api.riskDensityBy.data,
        api.riskDensityBy.data?.endProjectDate,
        api.riskDensityBy.data?.startProjectDate,
    ]);

    return {
        handleSelectBy,
        handleDateRangeSelection,
        handleResolutionFilter,
        serialized,
        setDateOnDelayDriver,
        selectByValue,
        resolutionValue,
    };
};
